import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { i18n } from '@bms/common';
import { Button } from '@/components/ui/button';

// ============================================================================
// ErrorBoundary — Per-page fallback UI with retry
// Wrap individual route elements (not the whole app) so one failed page
// doesn't blank the shell (sidebar, header, etc. stay mounted).
// ============================================================================

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, info.componentStack);
  }

  // Clearing the error re-renders `children`, which remounts the crashed
  // subtree fresh (React unmounts it when the boundary catches).
  handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center py-20 text-center px-6">
          <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <h3 className="text-title font-medium text-foreground mb-1">{i18n.t('shell.errorBoundary.title')}</h3>
          <p className="text-ui text-muted-foreground max-w-[320px] mb-4">
            {i18n.t('shell.errorBoundary.body')}
          </p>
          <Button variant="outline" size="sm" onClick={this.handleRetry}>
            {i18n.t('shell.errorBoundary.retry')}
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
